/**
 * Local cache of compressed That Open Fragments buffers.
 *
 * A record is keyed by the SHA-256 of its source. For an IFC source this is
 * the IFC hash, so reopening the same IFC can bypass tessellation while the
 * original IFC bytes remain available to rawIfc. For a native `.frag` source
 * the source hash is the hash of that file.
 *
 * A chave é o hash da ORIGEM, mas o registo guarda também a versão do
 * motor que o produziu, e um registo de outro motor é descartado na
 * leitura. Sem isso, a cache sobrevive a uma mudança de versão do
 * Fragments e continua a servir a derivação antiga.
 */

/** A nossa configuração do importador também muda o resultado, e não
 *  só a versão do motor. Quando se muda o que se converte (a
 *  2026-09-21, as IfcSpatialZone passaram a ter geometria; a 2026-09-22,
 *  os types e o IfcRelDefinesByType passaram a ser guardados; a
 *  2026-10-07, as GeometryProcessSettings deixaram de ser baixadas por
 *  engano e ficou só o threshold a 0; a 2026-10-19, o web-ifc passou a
 *  ser o compilado do main, que dá geometria aos sólidos varridos do
 *  IFC 4.3), sobe-se isto, e os modelos já convertidos voltam a
 *  converter-se na próxima abertura em vez de aparecerem sem o que falta.
 *  A versão do Fragments entra na chave sozinha; a do web-ifc não, por
 *  isso uma troca de web-ifc obriga a subir isto. */
const REVISAO_DA_CONVERSAO = 'conv5'

/** Versão do motor que produziu a geometria. Um `.frag` é geometria
 *  JÁ derivada: se o motor mudar, a cache continua a servir a derivação
 *  antiga e os bugs dessa versão sobrevivem à actualização sem deixar
 *  rasto. Aconteceu ao medir o 3.4.7 e a seguir o 3.4.3 sobre a mesma
 *  cache: as duas medições deram o mesmo resultado errado, porque a
 *  segunda nunca chegou a converter nada. */
const ENGINE_VERSION =
  (typeof __FRAGMENTS_VERSION__ === 'string' ? __FRAGMENTS_VERSION__ : 'unknown') +
  '+' +
  REVISAO_DA_CONVERSAO

/** Um registo em cache só serve se veio do motor que está a correr.
 *  Registos sem versão, ou com a versão de outro motor, não servem. */
export function cacheMatchesEngine(
  recordEngineVersion: string | undefined,
  engineVersion: string = ENGINE_VERSION,
): boolean {
  return !!recordEngineVersion && recordEngineVersion === engineVersion
}

const DB_NAME = 'vn2r.fragments'
const DB_VERSION = 1
const STORE = 'models'

export const DEFAULT_FRAGMENT_MAX_BYTES = 700 * 1024 * 1024

export interface FragmentBlobRecord {
  sourceSha256: string
  filename: string
  sourceFormat: 'ifc' | 'frag'
  sizeBytes: number
  blob: Blob
  createdAt: string
  lastUsedAt: string
  engineVersion: string
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE, { keyPath: 'sourceSha256' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => {
      dbPromise = null
      reject(request.error ?? new Error('IDB open failed'))
    }
  })
  return dbPromise
}

function requestPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () =>
      reject(request.error ?? new Error('IDB request failed'))
  })
}

async function inStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const db = await openDb()
  return await new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    let result: T
    run(tx.objectStore(STORE))
      .then((value) => {
        result = value
      })
      .catch(reject)
    tx.oncomplete = () => resolve(result)
    tx.onerror = () => reject(tx.error ?? new Error('IDB transaction failed'))
    tx.onabort = () => reject(tx.error ?? new Error('IDB transaction aborted'))
  })
}

export const fragmentPersistence = {
  async save(options: {
    sourceSha256: string
    filename: string
    sourceFormat: 'ifc' | 'frag'
    buffer: ArrayBuffer | Uint8Array
    engineVersion?: string
    maxBytes?: number
  }): Promise<boolean> {
    const copy =
      options.buffer instanceof Uint8Array
        ? options.buffer.slice().buffer
        : options.buffer.slice(0)
    const sizeBytes = copy.byteLength
    if (sizeBytes > (options.maxBytes ?? DEFAULT_FRAGMENT_MAX_BYTES)) {
      return false
    }
    const now = new Date().toISOString()
    const record: FragmentBlobRecord = {
      sourceSha256: options.sourceSha256.toLowerCase(),
      filename: options.filename,
      sourceFormat: options.sourceFormat,
      sizeBytes,
      blob: new Blob([copy], { type: 'application/octet-stream' }),
      createdAt: now,
      lastUsedAt: now,
      engineVersion: options.engineVersion ?? ENGINE_VERSION,
    }
    try {
      await inStore('readwrite', async (store) => {
        const existing = await requestPromise<FragmentBlobRecord | undefined>(
          store.get(record.sourceSha256),
        )
        if (existing) record.createdAt = existing.createdAt
        await requestPromise(store.put(record))
      })
      return true
    } catch (error) {
      console.warn('[fragmentPersistence] save failed', error)
      return false
    }
  },

  async load(sourceSha256: string): Promise<FragmentBlobRecord | null> {
    try {
      const record = await inStore('readonly', (store) =>
        requestPromise<FragmentBlobRecord | undefined>(
          store.get(sourceSha256.toLowerCase()),
        ),
      )
      if (!record) return null
      if (!cacheMatchesEngine(record.engineVersion)) {
        // Geometria derivada por outro motor. Apaga-se em vez de se
        // devolver: reconverter a partir do IFC custa segundos, servir
        // geometria errada custa uma investigação inteira.
        console.info(
          '[fragmentPersistence] cache de outro motor descartada',
          record.engineVersion,
          '->',
          ENGINE_VERSION,
        )
        await fragmentPersistence.remove(record.sourceSha256)
        return null
      }
      return record
    } catch {
      return null
    }
  },

  async buffer(record: FragmentBlobRecord): Promise<ArrayBuffer> {
    return await record.blob.arrayBuffer()
  },

  async touch(sourceSha256: string): Promise<void> {
    const record = await fragmentPersistence.load(sourceSha256)
    if (!record) return
    record.lastUsedAt = new Date().toISOString()
    try {
      await inStore('readwrite', (store) =>
        requestPromise(store.put(record)),
      )
    } catch {
      // Cache metadata is best-effort.
    }
  },

  async list(): Promise<FragmentBlobRecord[]> {
    try {
      return await inStore('readonly', (store) =>
        requestPromise(store.getAll() as IDBRequest<FragmentBlobRecord[]>),
      )
    } catch {
      return []
    }
  },

  async remove(sourceSha256: string): Promise<void> {
    try {
      await inStore('readwrite', (store) =>
        requestPromise(store.delete(sourceSha256.toLowerCase())),
      )
    } catch {
      // Cache cleanup is best-effort.
    }
  },

  async clearAll(): Promise<void> {
    try {
      await inStore('readwrite', (store) => requestPromise(store.clear()))
    } catch {
      // Cache cleanup is best-effort.
    }
  },
}

